import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Client routes mirror the server URLs. M4c part 1 adds the public journeys (events browse +
// detail, editorial content pages, dealers) and the member suite (account, schedule, wishlist,
// cart + checkout, registration, relationships, groups).
const routes: RouteRecordRaw[] = [
  { path: '/', name: 'home', component: () => import('@/views/HomeView.vue') },
  { path: '/login', name: 'login', component: () => import('@/views/LoginView.vue') },
  { path: '/register', name: 'register', component: () => import('@/views/RegisterView.vue') },
  {
    path: '/forgotpassword',
    name: 'forgot-password',
    component: () => import('@/views/ForgotPasswordView.vue'),
  },

  // ---- Events (public) --------------------------------------------------------------------
  { path: '/events', name: 'events', component: () => import('@/views/EventsBrowseView.vue') },
  {
    path: '/events/event/:id(\\d+)',
    name: 'event-detail',
    component: () => import('@/views/EventDetailView.vue'),
  },

  // ---- Editorial content pages (public) ---------------------------------------------------
  { path: '/about', name: 'about', component: () => import('@/views/AboutView.vue') },
  { path: '/faqs', name: 'faqs', component: () => import('@/views/FaqsView.vue') },
  { path: '/attend', name: 'attend', component: () => import('@/views/AttendView.vue') },
  { path: '/gminfo', name: 'gm-info', component: () => import('@/views/GmInfoView.vue') },
  { path: '/dealers', name: 'dealers', component: () => import('@/views/DealersView.vue') },
  {
    path: '/dealers/apply',
    name: 'dealers-apply',
    component: () => import('@/views/DealerApplyView.vue'),
  },
  { path: '/guests', name: 'guests', component: () => import('@/views/GuestsView.vue') },
  {
    path: '/attendancepolicies',
    name: 'attendance-policies',
    component: () => import('@/views/ContentPageView.vue'),
    meta: { contentKey: 'attendancepolicies', kicker: 'House rules' },
  },
  {
    path: '/history',
    name: 'history',
    component: () => import('@/views/ContentPageView.vue'),
    meta: { contentKey: 'history', kicker: 'The archive' },
  },
  {
    path: '/links',
    name: 'links',
    component: () => import('@/views/ContentPageView.vue'),
    meta: { contentKey: 'links', kicker: 'Around the hobby' },
  },

  // ---- Account suite (login required — see requiresLogin below) ----------------------------
  { path: '/account', name: 'account', component: () => import('@/views/AccountView.vue') },
  {
    path: '/account/profile',
    name: 'account-profile',
    component: () => import('@/views/ProfileEditView.vue'),
  },
  {
    path: '/account/password',
    name: 'account-password',
    component: () => import('@/views/PasswordChangeView.vue'),
  },
  {
    path: '/account/schedule',
    name: 'account-schedule',
    component: () => import('@/views/ScheduleView.vue'),
  },
  {
    path: '/account/groups/:id(\\d+)',
    name: 'account-group',
    component: () => import('@/views/UserGroupView.vue'),
  },
  {
    path: '/user/relationships',
    name: 'relationships',
    component: () => import('@/views/RelationshipsView.vue'),
  },
  {
    path: '/user/relationships/confirm/:code',
    name: 'friend-confirm',
    component: () => import('@/views/FriendConfirmView.vue'),
  },
  {
    path: '/user/groups/join/:code',
    name: 'group-join',
    component: () => import('@/views/UserGroupJoinView.vue'),
  },

  // ---- Wishlist / cart / registration (login required) -------------------------------------
  { path: '/wishlist', name: 'wishlist', component: () => import('@/views/WishlistView.vue') },
  { path: '/cart', name: 'cart', component: () => import('@/views/CartView.vue') },
  {
    path: '/cart/confirmation',
    name: 'cart-confirmation',
    component: () => import('@/views/CartConfirmationView.vue'),
  },
  {
    path: '/registration',
    name: 'registration',
    component: () => import('@/views/RegistrationView.vue'),
  },
  {
    path: '/registration/redeem/:code?',
    name: 'redeem-code',
    component: () => import('@/views/RedeemCodeView.vue'),
  },
  // ---- GM flow (login required) -------------------------------------------------------------
  {
    path: '/event',
    name: 'event-submit',
    component: () => import('@/views/EventSubmitView.vue'),
  },
  {
    path: '/event/mine',
    name: 'my-events',
    component: () => import('@/views/MyEventsView.vue'),
  },
  {
    path: '/event/:id(\\d+)/edit',
    name: 'event-edit',
    component: () => import('@/views/EventSubmitView.vue'),
  },

  // Admin (ADMIN-gated by the beforeEach guard below + the /api/v1/admin/** server chain).
  {
    path: '/admin',
    name: 'admin-dashboard',
    component: () => import('@/views/AdminDashboardView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/events',
    name: 'admin-events',
    component: () => import('@/views/AdminEventsView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    // Solr-backed admin search over approved events (same API as the public browse); rows open
    // the admin event form. Static segment — no collision with the numeric :id route below.
    path: '/admin/events/search',
    name: 'admin-events-search',
    component: () => import('@/views/AdminEventsSearchView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/events/:id(\\d+)',
    name: 'admin-event-form',
    component: () => import('@/views/AdminEventFormView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/slots',
    name: 'admin-slots',
    component: () => import('@/views/AdminSlotsView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/schedule',
    name: 'admin-schedule',
    component: () => import('@/views/AdminScheduleView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/registrations',
    name: 'admin-registrations',
    component: () => import('@/views/AdminRegistrationsView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/registrations/:id(\\d+)',
    name: 'admin-registration-form',
    component: () => import('@/views/AdminRegistrationFormView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/regtypes',
    name: 'admin-regtypes',
    component: () => import('@/views/AdminRegTypesView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/regtypes/:id(new|\\d+)',
    name: 'admin-regtype-form',
    component: () => import('@/views/AdminRegTypeFormView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/promocodes',
    name: 'admin-promocodes',
    component: () => import('@/views/AdminPromoCodesView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/regcodes',
    name: 'admin-regcodes',
    component: () => import('@/views/AdminRegCodesView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/users',
    name: 'admin-users',
    component: () => import('@/views/AdminUsersView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/users/:id(\\d+)',
    name: 'admin-user-form',
    component: () => import('@/views/AdminUserFormView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/guests',
    name: 'admin-guests',
    component: () => import('@/views/AdminGuestsView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/guests/:id(\\d+)',
    name: 'admin-guest-form',
    component: () => import('@/views/AdminGuestFormView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/groups',
    name: 'admin-groups',
    component: () => import('@/views/AdminGroupsView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/groups/:id(new|\\d+)',
    name: 'admin-group-form',
    component: () => import('@/views/AdminGroupFormView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/tables',
    name: 'admin-tables',
    component: () => import('@/views/AdminTablesView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/conventions',
    name: 'admin-conventions',
    component: () => import('@/views/AdminConventionsView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/conventions/:id(new|\\d+)',
    name: 'admin-convention-form',
    component: () => import('@/views/AdminConventionFormView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/transactions',
    name: 'admin-transactions',
    component: () => import('@/views/AdminTransactionsView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/dealer-applications',
    name: 'admin-dealer-applications',
    component: () => import('@/views/AdminDealerApplicationsView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/exhibitors',
    name: 'admin-exhibitors',
    component: () => import('@/views/AdminExhibitorsView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/exhibitors/:id(new|\\d+)',
    name: 'admin-exhibitor-form',
    component: () => import('@/views/AdminExhibitorFormView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/form-responses',
    name: 'admin-form-responses',
    component: () => import('@/views/AdminFormResponsesView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/activity',
    name: 'admin-activity',
    component: () => import('@/views/AdminActivityView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/tools',
    name: 'admin-tools',
    component: () => import('@/views/AdminToolsView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/content',
    name: 'admin-content-blocks',
    component: () => import('@/views/AdminContentBlocksView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/content/:id(new|\\d+)',
    name: 'admin-content-block-form',
    component: () => import('@/views/AdminContentBlockFormView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/style-guide',
    name: 'admin-style-guide',
    component: () => import('@/views/StyleGuideView.vue'),
    meta: { requiresAdmin: true },
  },
  { path: '/error', name: 'server-error', component: () => import('@/views/ErrorView.vue') },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
  },
]

// The same bundle is served under `/app` during the strangler period and at root `/` after the
// production cutover. Derive the router base from where the shell was actually served so one build
// works in both modes.
function detectSpaBase(): string {
  const p = window.location.pathname
  return p === '/app' || p.startsWith('/app/') ? '/app' : '/'
}

const router = createRouter({
  history: createWebHistory(detectSpaBase()),
  routes,
  scrollBehavior: () => ({ top: 0 }),
})

// Path prefixes that need a logged-in user (the /api/v1/** chain enforces the same server-side).
// `/event` (GM submission) is distinct from the public `/events` browse; `/dealers/apply` is the
// authenticated application inside the otherwise-public dealers section.
const AUTH_PREFIXES = [
  '/account',
  '/user',
  '/wishlist',
  '/cart',
  '/registration',
  '/event',
  '/dealers/apply',
] as const

export function isAdminPath(path: string): boolean {
  return path === '/admin' || path.startsWith('/admin/')
}

export function requiresLogin(path: string): boolean {
  if (isAdminPath(path)) return true
  return AUTH_PREFIXES.some((p) => path === p || path.startsWith(p + '/'))
}

// Client-side UX only: the /api/v1/** chain still enforces auth server-side, so no data leaks —
// but anonymous users get sent to the login page (with a redirect back) instead of loading a
// member page's chrome and then a "sign in" error.
router.beforeEach(async (to) => {
  if (!requiresLogin(to.path)) return true

  const auth = useAuthStore()
  if (!auth.loaded) await auth.loadMe()

  if (!auth.user?.authenticated) {
    // Send anonymous users to the SPA login, remembering where they were headed.
    return { path: '/login', query: { redirect: to.fullPath } }
  }
  // Admin gate: route meta (requiresAdmin) or the /admin/** path prefix — both are checked so a
  // future admin route can't slip through by forgetting the meta flag.
  if ((to.meta.requiresAdmin === true || isAdminPath(to.path)) && !auth.isAdmin) {
    return { path: '/' }
  }
  return true
})

export default router
